import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Phone, Mail, Car, Wallet, Maximize, Pencil, Plus, Trash2, LogOut,
  Users, ShieldCheck, CalendarRange, Loader2,
} from "lucide-react";
import { STATUS_META, formatCurrency, formatDate } from "@/lib/status";
import TenancyDialog from "@/components/TenancyDialog";
import MoveOutDialog from "@/components/MoveOutDialog";

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3 py-2">
    <div className="h-8 w-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
      <Icon className="h-4 w-4 text-slate-500" />
    </div>
    <div className="min-w-0">
      <p className="text-[11px] uppercase font-mono tracking-[0.14em] text-slate-400">{label}</p>
      <p className="text-sm text-slate-800 truncate">{value || "—"}</p>
    </div>
  </div>
);

export default function FlatDrawer({ flatId, open, onOpenChange, onChanged, onEdit }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [flat, setFlat] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tenancyOpen, setTenancyOpen] = useState(false);
  const [editTenancy, setEditTenancy] = useState(null);
  const [moveOutFor, setMoveOutFor] = useState(null);

  const load = async () => {
    if (!flatId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/flats/${flatId}`);
      setFlat(data);
    } catch {
      toast.error("Failed to load flat");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open && flatId) load(); }, [open, flatId]);

  const refresh = async () => { await load(); onChanged?.(); };

  const deleteTenancy = async (id) => {
    try {
      await api.delete(`/tenancies/${id}`);
      toast.success("Tenancy removed");
      refresh();
    } catch { toast.error("Delete failed"); }
  };

  const deleteFlat = async () => {
    if (!window.confirm("Delete this flat and all its tenancy records?")) return;
    try {
      await api.delete(`/flats/${flatId}`);
      toast.success("Flat deleted");
      onOpenChange(false);
      onChanged?.();
    } catch { toast.error("Delete failed"); }
  };

  const meta = flat ? (STATUS_META[flat.effective_status] || STATUS_META.vacant) : STATUS_META.vacant;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-xl p-0 overflow-hidden" data-testid="flat-drawer">
        {loading || !flat ? (
          <div className="flex items-center justify-center h-full text-slate-400 gap-2">
            <Loader2 className="h-5 w-5 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="flex flex-col h-full">
            <SheetHeader className="p-6 border-b border-slate-200 space-y-0">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-[11px] uppercase font-mono tracking-[0.18em] text-slate-400">
                    Tower {flat.tower} · Floor {flat.floor}
                  </p>
                  <SheetTitle className="font-display text-2xl font-extrabold text-slate-900">
                    Flat {flat.flat_number}
                  </SheetTitle>
                  <div className="flex items-center gap-2 mt-2">
                    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${meta.badge}`}>
                      <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} /> {meta.label}
                    </span>
                    <span className="text-xs font-mono text-slate-500">{flat.bhk_type}</span>
                  </div>
                </div>
                {isAdmin && (
                  <div className="flex gap-2">
                    <Button size="icon" variant="outline" onClick={() => onEdit?.(flat)} data-testid="drawer-edit-flat-button"><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="outline" className="text-red-600 hover:text-red-700" onClick={deleteFlat} data-testid="drawer-delete-flat-button"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                )}
              </div>
            </SheetHeader>

            <ScrollArea className="flex-1">
              <div className="p-6 space-y-8">
                {/* Owner dossier */}
                <section>
                  <h3 className="font-display text-sm font-semibold text-slate-900 mb-2">Owner Dossier</h3>
                  <div className="rounded-xl border border-slate-200 p-4">
                    <p className="font-semibold text-slate-900">{flat.owner_name}</p>
                    <div className="grid grid-cols-2 gap-x-4 mt-1">
                      <InfoRow icon={Phone} label="Phone" value={flat.owner_phone} />
                      <InfoRow icon={Mail} label="Email" value={flat.owner_email} />
                      <InfoRow icon={Car} label="Parking" value={flat.parking_slot} />
                      <InfoRow icon={Maximize} label="Area" value={flat.sqft_area ? `${flat.sqft_area} sq.ft` : null} />
                      <InfoRow icon={Wallet} label="Maintenance" value={flat.maintenance_amount ? `${formatCurrency(flat.maintenance_amount)}/mo` : null} />
                      <InfoRow icon={Users} label="Furnishing" value={flat.furnishing_status} />
                      <InfoRow icon={ShieldCheck} label="Possession Letter" value={flat.possession_letter_no} />
                      <InfoRow icon={CalendarRange} label="Handover Date" value={flat.handover_date ? formatDate(flat.handover_date) : null} />
                    </div>
                    {!flat.handover_date && (
                      <p className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="handover-warning">
                        Handover details missing — edit this flat to add the possession letter &amp; handover date before letting a tenant in.
                      </p>
                    )}
                  </div>
                </section>

                {/* Telemetry */}
                <section className="grid grid-cols-3 gap-3">
                  <div className="rounded-xl border border-slate-200 p-4 text-center">
                    <p className="text-2xl font-bold font-mono text-slate-900 tabular-nums">{flat.times_rented}</p>
                    <p className="text-[10px] uppercase font-mono tracking-[0.14em] text-slate-400 mt-1">Times Rented</p>
                  </div>
                  <div className="rounded-xl border border-slate-200 p-4 text-center">
                    <p className="text-2xl font-bold font-mono text-slate-900 tabular-nums">
                      {flat.tenancies.filter((t) => t.status === "active").length}
                    </p>
                    <p className="text-[10px] uppercase font-mono tracking-[0.14em] text-slate-400 mt-1">Active Now</p>
                  </div>
                  <div className="rounded-xl border border-slate-200 p-4 text-center">
                    <p className="text-2xl font-bold font-mono text-slate-900 tabular-nums">
                      {flat.tenancies.filter((t) => t.status === "past").length}
                    </p>
                    <p className="text-[10px] uppercase font-mono tracking-[0.14em] text-slate-400 mt-1">Past Tenants</p>
                  </div>
                </section>

                {/* Tenancy timeline */}
                <section>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-display text-sm font-semibold text-slate-900">Move-in / Move-out History</h3>
                    <Button size="sm" className="bg-[#0F2942] gap-1.5 h-8" onClick={() => setTenancyOpen(true)} disabled={!flat.handover_date} title={!flat.handover_date ? "Add flat handover details first" : ""} data-testid="add-tenancy-button">
                      <Plus className="h-3.5 w-3.5" /> Add Tenancy
                    </Button>
                  </div>

                  {flat.tenancies.length === 0 ? (
                    <p className="text-sm text-slate-400 py-6 text-center border border-dashed border-slate-200 rounded-xl">
                      No tenancy records yet. This flat has never been rented out.
                    </p>
                  ) : (
                    <div className="relative pl-6 space-y-5 before:absolute before:left-[7px] before:top-1 before:bottom-1 before:w-px before:bg-slate-200">
                      {flat.tenancies.map((t) => {
                        const active = t.status === "active";
                        return (
                          <div key={t.id} className="relative" data-testid={`tenancy-record-${t.id}`}>
                            <span className={`absolute -left-[22px] top-1.5 h-3.5 w-3.5 rounded-full border-2 border-white ${active ? "bg-emerald-500" : "bg-slate-300"}`} />
                            <div className="rounded-xl border border-slate-200 p-4">
                              <div className="flex items-start justify-between gap-2">
                                <div>
                                  <div className="flex items-center gap-2">
                                    <p className="font-semibold text-slate-900">{t.tenant_name}</p>
                                    <span className={`rounded-full border px-2 py-0.5 text-[10px] font-medium ${active ? STATUS_META.rented.badge : "bg-slate-100 text-slate-500 border-slate-200"}`}>
                                      {active ? "Active" : "Past"}
                                    </span>
                                  </div>
                                  <p className="text-xs text-slate-400 font-mono mt-0.5">{t.tenant_phone}</p>
                                </div>
                                {isAdmin && (
                                  <div className="flex gap-1">
                                    <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-500" title="Edit tenancy" onClick={() => setEditTenancy(t)} data-testid={`edit-tenancy-${t.id}`}>
                                      <Pencil className="h-3.5 w-3.5" />
                                    </Button>
                                    {active && (
                                      <Button size="icon" variant="ghost" className="h-7 w-7 text-amber-600" title="Record move-out" onClick={() => setMoveOutFor(t)} data-testid={`moveout-button-${t.id}`}>
                                        <LogOut className="h-3.5 w-3.5" />
                                      </Button>
                                    )}
                                    <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => deleteTenancy(t.id)} data-testid={`delete-tenancy-${t.id}`}>
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                  </div>
                                )}
                              </div>
                              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 mt-3 text-xs">
                                <span className="flex items-center gap-1.5 text-slate-600"><CalendarRange className="h-3 w-3 text-slate-400" /> In: {formatDate(t.move_in_date)}</span>
                                <span className="flex items-center gap-1.5 text-slate-600"><CalendarRange className="h-3 w-3 text-slate-400" /> Out: {formatDate(t.move_out_date)}</span>
                                <span className="flex items-center gap-1.5 text-slate-600"><Wallet className="h-3 w-3 text-slate-400" /> Rent: {formatCurrency(t.monthly_rent)}</span>
                                <span className="flex items-center gap-1.5 text-slate-600"><Wallet className="h-3 w-3 text-slate-400" /> Deposit: {formatCurrency(t.security_deposit)}</span>
                                {t.family_members != null && <span className="flex items-center gap-1.5 text-slate-600"><Users className="h-3 w-3 text-slate-400" /> {t.family_members} members</span>}
                                {t.id_proof_type && <span className="flex items-center gap-1.5 text-slate-600"><ShieldCheck className="h-3 w-3 text-slate-400" /> {t.id_proof_type} {t.id_proof_number}</span>}
                                {t.move_in_charge_amount != null && <span className="flex items-center gap-1.5 text-emerald-700"><Wallet className="h-3 w-3" /> Move-in: {formatCurrency(t.move_in_charge_amount)} ({t.move_in_payment_mode}{t.move_in_transaction_ref ? ` · ${t.move_in_transaction_ref}` : ""})</span>}
                                {t.move_out_charge_amount != null && <span className="flex items-center gap-1.5 text-rose-700"><Wallet className="h-3 w-3" /> Move-out: {formatCurrency(t.move_out_charge_amount)} ({t.move_out_payment_mode}{t.move_out_transaction_ref ? ` · ${t.move_out_transaction_ref}` : ""})</span>}
                                {(t.move_in_charge_amount != null || t.move_out_charge_amount != null) && <span className="flex items-center gap-1.5 font-semibold text-slate-800"><Wallet className="h-3 w-3" /> Total paid: {formatCurrency((t.move_in_charge_amount || 0) + (t.move_out_charge_amount || 0))}</span>}
                              </div>
                              {(t.lease_start_date || t.emergency_contact_name || t.remarks) && (
                                <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-500 space-y-1">
                                  {t.lease_start_date && <p>Lease: {formatDate(t.lease_start_date)} → {formatDate(t.lease_end_date)}</p>}
                                  {t.emergency_contact_name && <p>Emergency: {t.emergency_contact_name} · {t.emergency_contact_phone}</p>}
                                  <p>Rent status: <span className="font-medium">{t.rent_paid_status}</span>{t.deposit_refunded != null && ` · Deposit ${t.deposit_refunded ? "refunded" : "withheld"}`}</p>
                                  {t.remarks && <p className="italic">“{t.remarks}”</p>}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </section>
              </div>
            </ScrollArea>
          </div>
        )}

        {flat && (
          <>
            <TenancyDialog open={tenancyOpen} onOpenChange={setTenancyOpen} flat={flat} onSaved={refresh} />
            <TenancyDialog open={!!editTenancy} onOpenChange={(v) => !v && setEditTenancy(null)} flat={flat} editTenancy={editTenancy} onSaved={refresh} />
            <MoveOutDialog open={!!moveOutFor} onOpenChange={(v) => !v && setMoveOutFor(null)} tenancy={moveOutFor} onSaved={refresh} />
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
